import type {ReactElement} from 'react';
import React, {useState} from 'react';
import {ControlledList} from '@Components/controlled-list';
import {CONTROLLED_LIST_ITEM_TYPE} from '@Components/controlled-list/controlled-list.types';
import {ClickableDiv} from '@Components/clickable-div';
import {Text, TextSize} from '@Components/text';
import {ControlledSlider} from '@Components/controlled-slider';
import {ThumbnailType} from '@Components/thumbnail/thumbnail.types';
import {AddItemThumbnail} from '@Components/poster-editor/components/poster-add-item-bar/components/add-item-thumbnail';
import ControlledDurationInput from '@Components/controlled-duration-input/controlled-duration-input';
import styles from './custom-duration-item.module.scss';
import {useGetPageMinDuration} from '@Hooks/poster-editor/useGetPageMinDuration';

const PAGE_DURATION_MAX = 600; // 10 minutes — practical UI cap for page duration slider

interface CustomDurationItemProps {
  isSelected?: boolean;
  currentPageDuration?: number;
  onChange(): void;
  updateDuration(value: number, undoable?: boolean): void;
  dontShowSelectedStateIfSelected?: boolean;
  isMobileVersion?: boolean;
}

export function CustomDurationItem({isSelected = false, isMobileVersion = false, dontShowSelectedStateIfSelected = false, ...props}: CustomDurationItemProps): ReactElement<any> | null {
  const [durationWhileSliding, setDurationWhileSliding] = useState(props.currentPageDuration);
  const minDuration = useGetPageMinDuration();
  const [isSliding, setIsSliding] = useState(false);
  const getCustomDurationUnSelectedVersion = (): ReactElement<any> => {
    return (
      <ControlledList
        className={`${!isMobileVersion && isSelected ? '_hidden' : ''} spacing-m-0`}
        items={[
          {
            id: 'custom-duration-unselected-item',
            type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_2,
            onClick: (): void => {
              props.onChange();
            },
            text: window.i18next.t('pmwjs_custom'),
            subText: window.i18next.t('pmwjs_select_custom_duration'),
            thumbnail: {
              type: ThumbnailType.CUSTOM,
              content: <AddItemThumbnail iconClassName={styles.icon} className={styles.customThumbnail} icon="icon-clock-o" />,
            },
          },
        ]}
      />
    );
  };

  const onDurationChange = (value: number): void => {
    if (!isSelected) {
      return;
    }

    if (!isSliding) {
      setIsSliding(true);
    }

    setDurationWhileSliding(value);
  };

  const onDurationChangeEnded = (value: number): void => {
    if (!isSelected) {
      return;
    }

    setIsSliding(false);
    setDurationWhileSliding(value);
    props.updateDuration(value, true);
  };

  const getDurationValue = (isForInputField = false): number => {
    let duration = props.currentPageDuration ?? minDuration;
    if (isSliding && durationWhileSliding) {
      duration = durationWhileSliding;
    }

    if (isForInputField) {
      return Math.round(duration);
    }
    return duration;
  };

  const getSelectedCustomDurationItem = (): ReactElement<any> => {
    return (
      <ClickableDiv
        onClick={(): void => {
          props.onChange();
        }}
        className={`flex-v-row spacing-p-3 radius-4 hover-transition-all ${!isMobileVersion ? 'spacing-m-b-2' : ''} ${!isMobileVersion && !isSelected ? '_hidden' : ''} ${
          styles.customDurationContainer
        } ${dontShowSelectedStateIfSelected ? styles.noBackground : styles.selectedCustomOption}`}
      >
        <div className="flex-row-justify-between flex-items-center spacing-m-b-3">
          <Text
            className={dontShowSelectedStateIfSelected ? '' : 'content-primary'}
            bold={!dontShowSelectedStateIfSelected}
            val={window.i18next.t('pmwjs_custom')}
            size={TextSize.XSMALL}
          />
          <ControlledDurationInput
            id="poster-custom-duration"
            duration={getDurationValue(true)}
            onDurationChanged={onDurationChangeEnded}
            maxDuration={PAGE_DURATION_MAX}
            minDuration={minDuration}
          />
        </div>
        <ControlledSlider
          value={getDurationValue()}
          min={minDuration}
          max={PAGE_DURATION_MAX}
          onSlide={onDurationChange}
          onSlideEnded={onDurationChangeEnded}
          className={dontShowSelectedStateIfSelected ? '' : 'selected-custom-duration-slider'}
        />
      </ClickableDiv>
    );
  };

  if (isMobileVersion) {
    return isSelected ? getSelectedCustomDurationItem() : getCustomDurationUnSelectedVersion();
  }

  return (
    <>
      {getSelectedCustomDurationItem()}
      {getCustomDurationUnSelectedVersion()}
    </>
  );
}
